import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Grid,
  CircularProgress,
  createFilterOptions,
} from '@mui/material';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { COUNTRIES_JSON } from '../../utils/locations';

type Country = { id: number; name: string };

const filterOptions = createFilterOptions<string>({ limit: 10 });

export type CountryInputProps = {
  register: UseFormRegister<any>;
  registerValue: string;
  setValue: UseFormSetValue<any>;
  handleCountyCodeChange: (code: number) => void;
  id: string;
  originalCountry?: string;
  plainInput?: boolean;
  label?: string;
};

function CountryInput({
  register,
  registerValue,
  setValue,
  handleCountyCodeChange,
  originalCountry,
  id,
  plainInput = false,
  label = 'Country',
}: CountryInputProps) {
  const [countries, setCountries] = useState<Country[]>([]);
  const [options, setOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const byName = useMemo(() => {
    const map = new Map<string, Country>();
    for (const c of countries) map.set(c.name, c);
    return map;
  }, [countries]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);

    fetch(COUNTRIES_JSON, { signal: ctrl.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load countries (${res.status})`);
        return res.json() as Promise<Country[]>;
      })
      .then((data) => {
        setCountries(data);
        setOptions(data.map((c) => c.name));

        const initializeTo = originalCountry ?? 'United States';
        if (initializeTo) {
          setInputValue(initializeTo);
          setValue(registerValue, initializeTo);

          const match = data.find((c) => c.name === initializeTo);
          if (match) handleCountyCodeChange(match.id);
        }
      })
      .catch((err: any) => {
        if (err?.name !== 'AbortError') {
          console.error('Error loading countries:', err);
          setCountries([]);
          setOptions([]);
        }
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [setValue, handleCountyCodeChange, originalCountry, registerValue]);

  const applyValue = (val: string) => {
    setInputValue(val);
    setValue(registerValue, val);

    const match = byName.get(val);
    if (match) handleCountyCodeChange(match.id);
  };

  return (
    <Grid item xs={12} md={6}>
      {plainInput ? (
        <TextField
          {...register(registerValue)}
          fullWidth
          id={id}
          margin="normal"
          label={label}
          required
          autoComplete="off"
          value={inputValue}
          onChange={(e) => applyValue(e.target.value)}
          InputProps={{
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
              </>
            ),
          }}
        />
      ) : (
        <Autocomplete<string, false, false, true>
          freeSolo
          options={options}
          filterOptions={filterOptions}
          loading={loading}
          inputValue={inputValue}
          onInputChange={(_, newInput) => applyValue(newInput)}
          onChange={(_, newValue) => applyValue(newValue ?? '')}
          renderInput={(params) => (
            <TextField
              {...params}
              {...register(registerValue)}
              fullWidth
              id={id}
              margin="normal"
              label={label}
              required
              autoComplete="off"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    </Grid>
  );
}

export default CountryInput;
